import React, { useEffect, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowUpRightIcon, MailIcon, MessageCircleIcon, MessageSquareIcon, PhoneIcon } from 'lucide-react';
import { SubpageHeader } from '../components/SubpageHeader';
import { channels, Channel, partnerCities, privateLineHero, quickRequests } from '../data/privateLine';
import { contact } from '../data/contact';
const ease = [0.22, 1, 0.36, 1] as const;
const icons: Record<Channel['id'], React.ElementType> = {
  call: PhoneIcon,
  whatsapp: MessageCircleIcon,
  email: MailIcon,
  sms: MessageSquareIcon
};
export function PrivateLine() {
  const reduceMotion = useReducedMotion();
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Private Line — Superluxury';
    const id = window.setInterval(() => setNow(new Date()), 30000);
    return () => window.clearInterval(id);
  }, []);
  const rise = (delay = 0) => ({
    initial: {
      opacity: 0,
      y: reduceMotion ? 0 : 16
    },
    animate: {
      opacity: 1,
      y: 0
    },
    transition: {
      duration: 1.1,
      ease,
      delay
    }
  });
  const openWhatsApp = (message: string) => window.open(`https://wa.me/${contact.whatsappNumber}?text=${encodeURIComponent(message)}`, '_blank', 'noopener,noreferrer');
  return <div className="bg-ink text-paper" data-id="element-1563" data-mp="3demppwxtpenc">
      <SubpageHeader tone="dark" overlay data-id="element-1564" data-mp="3dbgz2d9jq4pi" />

      <main data-id="element-1565" data-mp="2pnh7xc8bocae">
        {/* Opening */}
        <section aria-labelledby="pl-title" className="relative flex min-h-[560px] items-end overflow-hidden md:min-h-[72vh]" data-id="element-1566" data-mp="2poiqe1ro4125">
          <motion.img src={privateLineHero.image} alt={privateLineHero.alt} className="absolute inset-0 h-full w-full object-cover" initial={{
          opacity: 0,
          scale: reduceMotion ? 1 : 1.06
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 2.2,
          ease
        }} data-id="element-1567" data-mp="2lkknrh34w84c" />
          <div className="absolute inset-0 bg-[linear-gradient(to_top,#000_0%,rgba(0,0,0,0.55)_45%,rgba(0,0,0,0.45))]" aria-hidden="true" data-id="element-1568" data-mp="21xd9dj9zr2q9" />
          <div className="film-grain pointer-events-none absolute inset-[-50%] opacity-[0.08] motion-reduce:hidden" aria-hidden="true" data-id="element-1569" data-mp="5t5qnulnchzs" />

          <div className="relative z-[1] mx-auto w-full max-w-[1440px] px-6 pb-14 md:px-16 md:pb-20" data-id="element-1570" data-mp="fkjn1tiax1op">
            <motion.p {...rise(0.2)} className="flex items-center gap-2.5 text-[14px] font-light text-paper/80" data-id="element-1571" data-mp="kko35ifaruee">
              <span className="relative flex h-2 w-2" aria-hidden="true" data-id="element-1572" data-mp="3j2tqhbro86fe">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-paper/50 motion-reduce:hidden" data-id="element-1573" data-mp="3o0mxg70st4gq" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-paper" data-id="element-1574" data-mp="3sywtsky9eb3x" />
              </span>
              A partner is on the line now
            </motion.p>
            <motion.h1 {...rise(0.35)} id="pl-title" className="mt-5 font-editorial text-[64px] leading-[64px] tracking-[-0.015em] md:text-[120px] md:leading-[112px]" data-id="element-1575" data-mp="18aa1k3nui5lc">
              {privateLineHero.title}
            </motion.h1>
            <motion.p {...rise(0.5)} className="mt-6 max-w-[560px] text-[19px] font-light leading-[30px] text-paper/85 md:text-[22px] md:leading-[33px]" data-id="element-1576" data-mp="2ws36eu8zkaqx">
              {privateLineHero.line}
            </motion.p>
          </div>
        </section>

        {/* Channels */}
        <section aria-label="Ways to reach your partner" className="px-6 md:px-16" data-id="element-1577" data-mp="2j2hv861fgztg">
          <ul className="mx-auto max-w-[1440px] border-t border-paper/15" data-id="element-1578" data-mp="2e2n4c2hnka8e">
            {channels.map((c, i) => {
            const Icon = icons[c.id];
            const external = c.href.startsWith('http');
            return <motion.li key={c.id} initial={{
              opacity: 0,
              y: reduceMotion ? 0 : 12
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              amount: 0.4
            }} transition={{
              duration: 0.9,
              ease,
              delay: i * 0.05
            }} className="border-b border-paper/15" data-id="element-1579" data-mp="252zzt7tmmob4" data-mpi={i}>
                  <a href={c.href} {...external ? {
                target: '_blank',
                rel: 'noopener noreferrer'
              } : {}} className="group grid grid-cols-1 items-center gap-4 py-8 transition-colors duration-300 md:grid-cols-12 md:gap-8 md:py-10" data-id="element-1580" data-mp="2r5zhexg2zx3h">
                    <span className="flex items-center gap-5 md:col-span-4" data-id="element-1581" data-mp="2n3kyyegbqzcq">
                      <Icon className="h-5 w-5 shrink-0 text-paper/60 transition-colors duration-300 group-hover:text-paper" strokeWidth={1.25} aria-hidden="true" data-id="element-1582" data-mp="28f7xj1ygwoau" />
                      <span className="font-editorial text-[40px] leading-[44px] md:text-[56px] md:leading-[58px]" data-id="element-1583" data-mp="2d8avdmaa2jwu">{c.name}</span>
                    </span>
                    <span className="md:col-span-5" data-id="element-1584" data-mp="23hohpxm4g3gs">
                      <span translate="no" dir="ltr" className="block text-[20px] font-light tabular-nums md:text-[24px]" data-id="element-1585" data-mp="1pgvmfanzx4cm">{c.value}</span>
                      <span className="mt-1 block text-[14px] font-light text-paper/60" data-id="element-1586" data-mp="1uex53uhif1nk">{c.response}</span>
                    </span>
                    <span className="inline-flex items-center gap-2 justify-self-start text-[16px] font-medium md:col-span-3 md:justify-self-end" data-id="element-1587" data-mp="1auf3pbszmgmp">
                      {c.action}
                      <ArrowUpRightIcon className="h-4 w-4 transition-transform duration-500 ease-luxe group-hover:-translate-y-0.5 group-hover:translate-x-0.5" strokeWidth={1.5} aria-hidden="true" data-id="element-1588" data-mp="1kt4eylqdp2tz" />
                    </span>
                  </a>
                </motion.li>;
          })}
          </ul>
        </section>

        {/* One-tap requests */}
        <section aria-labelledby="pl-quick" className="px-6 py-20 md:px-16 md:py-28" data-id="element-1589" data-mp="1yj7s8n7a5m7a">
          <div className="mx-auto grid max-w-[1440px] grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-16" data-id="element-1590" data-mp="2cj94eyszwg22">
            <div className="lg:col-span-4" data-id="element-1591" data-mp="28i71o7zvdlvc">
              <h2 id="pl-quick" className="font-editorial text-[36px] leading-[40px] tracking-[-0.01em] md:text-[44px] md:leading-[48px]" data-id="element-1592" data-mp="28j8heprtywje">
                No need to explain
              </h2>
              <p className="mt-4 max-w-[360px] text-[16px] font-light leading-[26px] text-paper/70" data-id="element-1593" data-mp="2kkzyh9ilfoe3">
                Tap a line and it arrives with your partner on WhatsApp, already written. Share your location and the rest is handled.
              </p>
            </div>
            <ul className="grid grid-cols-1 gap-px bg-paper/15 sm:grid-cols-2 lg:col-span-8 lg:grid-cols-3" data-id="element-1594" data-mp="2yc4snw36zoh1">
              {quickRequests.map(q => <li key={q.label} className="bg-ink" data-id="element-1595" data-mp="33css965hldmv">
                  <button type="button" onClick={() => openWhatsApp(q.message)} className="group flex h-full w-full items-center justify-between gap-4 px-6 py-7 text-left transition-colors duration-300 hover:bg-paper hover:text-ink" data-id="element-1596" data-mp="389j7xsddq77r">
                    <span className="text-[19px] font-light" data-id="element-1597" data-mp="1ivntq3pom6g9">{q.label}</span>
                    <span aria-hidden="true" className="text-[18px] transition-transform duration-500 ease-luxe group-hover:translate-x-1" data-id="element-1598" data-mp="9s940gyxr4">
                      →
                    </span>
                  </button>
                </li>)}
            </ul>
          </div>
        </section>

        {/* Partners on the ground */}
        <section aria-labelledby="pl-cities" className="border-t border-paper/15 px-6 py-14 md:px-16 md:py-16" data-id="element-1599" data-mp="3ifkge5tm4x6s">
          <div className="mx-auto flex max-w-[1440px] flex-col gap-8 lg:flex-row lg:items-end lg:justify-between" data-id="element-1600" data-mp="iwi2km8c9yta">
            <div data-id="element-1601" data-mp="9xxs28cxmvg5">
              <h2 id="pl-cities" className="text-[15px] font-light text-paper/65" data-id="element-1602" data-mp="9xejvjd33lfb">
                Partners on the ground, right now
              </h2>
              <ul className="mt-5 flex flex-wrap gap-x-12 gap-y-5" data-id="element-1603" data-mp="4yl6rs1plwfo">
                {partnerCities.map(c => <li key={c.city} data-id="element-1604" data-mp="2ulsiuy72i90j">
                    <span className="block text-[13px] font-light text-paper/55" data-id="element-1605" data-mp="2poiu7kri61dr">{c.city}</span>
                    <span className="mt-0.5 block text-[22px] font-light tabular-nums" data-id="element-1606" data-mp="tle5wdzow17y">
                      {new Intl.DateTimeFormat('en-GB', {
                    hour: '2-digit',
                    minute: '2-digit',
                    timeZone: c.timeZone
                  }).format(now)}
                    </span>
                  </li>)}
              </ul>
            </div>
            <p className="max-w-[420px] text-[14px] font-light leading-[22px] text-paper/60" data-id="element-1607" data-mp="onvy4qxers1j">
              Every conversation is held under NDA. Support in {contact.supportLanguage}; other languages on request.
            </p>
          </div>
        </section>
      </main>
    </div>;
}